import type { WritableDraft } from 'immer/src/types/types-external';
import type { TRunWithPayload } from 'pullstate';

import type { Method, RequestSchema } from '@/types/request';

export type { WritableDraft };

export interface UseRequestOpts<T, Q> {
  /** If false (default), the request is blocking and runs immediately upon mount */
  manual?: boolean;
  /** If false (default), the request will run on server as well. If also not `manual`, it is a blocking SSR request. */
  clientOnly?: boolean;
  // @todo somehow cast this better
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatResult?: (data: any) => T;
  /** HTTP method */
  method?: Method;
  /** URL query string parameters */
  query?: Q;
  /** If false (default), shows error/warning alert popups */
  hideAlert?: boolean;
  /** If false (default), attempts to refresh session token preemptively before your request when necessary */
  skipRefresh?: boolean;
  /** If false (default), tries to use `jsonResponse.data` as `data` if available */
  storeRaw?: boolean;
}

export type WithNew<Ty extends API.DbItem> = Ty & {
  isNew?: boolean;
};

export interface UseInfiniteRequestOpts<
  T extends API.DbItem,
  BodyType,
  Q,
  M,
  FromSocketType,
  InitialInput extends RequestInfo = RequestInfo,
> {
  /** Defaults to "id"; "createdAt" is another option */
  cursorKey?: keyof T;
  /** Event name to use for WebSocket updates */
  event?: string;
  /** Return false to short-circuit, return new data to override json, or return nothing to keep json same */
  eventValidate?: (
    event: MessageEvent,
    json:
      | API.SubscriptionResponse<WithNew<T>, string, FromSocketType>
      | API.WebsocketResult<API.Id>,
    knownIds: React.MutableRefObject<Set<number>>,
  ) =>
    | boolean
    | undefined
    | void
    | API.SubscriptionResponse<T>
    | Promise<boolean | undefined | void | API.SubscriptionResponse<T>>;
  /** Used in SSR */
  fetch?: typeof fetch;
  /** Unique key for this request set; when this changes, request will re-run from scratch. In addition, as long as it does not contain ':', it will be appended with the JSON.stringified content of `query`. In that case, when `query` changes, the request will also re-fetch. */
  key: string;
  /** Request schema */
  req: RequestSchema<API.WithPage<T[], M>, BodyType, InitialInput, Q> | string;
  /** Retain last result while changing; defaults to true unless key changes */
  holdPrevious?: boolean;
  /** Query to run for the initial data set; if not set, uses `query` */
  initialQuery?: Q;
  /** If true, stops the initial request from running at all */
  pause?: boolean;
  /** Change polling delays */
  PollingDelays?: Record<keyof typeof PollingDelaysDefault, number>;
  /** Query to run on subsequent fetches */
  query?: Q;
  /** If false (default), the initial request will run on client only */
  serverSkip?: boolean;
  /** When true, we don't use the fallback polling mechanism */
  skipPoll?: boolean;
  readonly __type?: T;
  readonly __btype?: BodyType;
}

export type UseInfiniteRequestResultData<T, M> = API.WithPage<T[], M> | null | undefined;

export interface UseInfiniteRequestItem<T, M> {
  data: UseInfiniteRequestResultData<T, M>;
  error: Error | null | undefined;
  isEmpty: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  isLoading: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
  isRefetching: boolean;
  isSuccess: boolean;
}

export interface UseInfiniteRequestResult<T, M> extends UseInfiniteRequestItem<T, M> {
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  newestItem: T | undefined;
  oldestItem: T | undefined;
  refetch: () => void;
  renderPayload: TRunWithPayload<API.WithPage<T[], M>>;
  reset: () => void;
  wsConnected: boolean;
}

export enum PollingDelaysDefault {
  // Just started and have WS connected
  WS_CONNECTED_START = 1000,
  // Just found a message over non-WS and WS connected
  WS_CONNECTED_JUST_RECEIVED = 2000,
  // No data for a long time and WS connected
  WS_CONNECTED_MAX = 180000,
  // Out-of-business hours
  WS_CONNECTED_MAX_OOB = 300000,
  // Increase after each check by this amount
  WS_CONNECTED_INCREMENT = 3000,
  START = 999,
  JUST_RECEIVED = 2500,
  MAX = 31000,
  // Out-of-business hours
  MAX_OOB = 60000,
  INCREMENT = 500,
  ERROR_INCREMENT = 5000,
}

export function isNewDbItem<T = unknown>(item: T): item is WithNew<API.DbItem<T>> {
  return (item as API.DbItemBase).id ? true : false;
}

export type NonNullableKeys<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};

export interface InfiniteStore<T, M> {
  banks: { [key: string]: UseInfiniteRequestItem<T, M> };
  get: (key: string) => UseInfiniteRequestResultData<T, M>;
  set: (
    key: string,
    cb: (
      draft: WritableDraft<NonNullableKeys<UseInfiniteRequestItem<T, M>, 'data'>>,
      original: UseInfiniteRequestResultData<T, M>,
      // shouldReplace?: false,
    ) => void,
  ) => void;
  remove: (key: string) => void;
}
